import { useSocket } from 'contexts/socketcontext/hooks';
import { useUserInformationContext } from 'contexts/userContext';
import { useWorkspaceContext } from 'contexts/workspace-context';
import debounce from 'debounce';
import { listOrders } from 'graphql/orders';
import { useInfoUpdateEvent } from 'hooks/useInfoUpdateEvent';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { ListOrdersParams } from 'types/common';
import { OrderListingType } from 'types/order';
import { infoUpdateType } from 'types/notifications';
import {
  doesBrowserSupportsDesktopNotifications,
  getPermissionState,
  PermissionState,
  requestNotificationPermission,
} from 'utils/notifications';
import { buildDesktopContent } from 'utils/notifications/content';
import { decideChannel, resolveOrderPath, showDesktopNotification } from 'utils/notifications/delivery';
import { createDedupe, evaluateEvent } from 'utils/notifications/filter';
import { appendHistory, clearHistory, markAllViewed } from 'utils/notifications/history';
import titleIcon from '../../../../assets/images/star-circle.svg';

const useLayoutHeader = () => {
  const { socket } = useSocket();
  const [isConnectedSocket, setIsConnectedSocket] = useState(false);
  const [notifications, setNotifications] = useState<infoUpdateType[]>([]);
  const [newNotification, setNewNotification] = useState<infoUpdateType>();
  const { openedOrderIdList } = useWorkspaceContext();
  const { currentUser } = useUserInformationContext();

  const { t } = useTranslation();
  const history = useHistory();

  const tabsRef = useRef(openedOrderIdList);
  const historyRef = useRef(history);
  const tRef = useRef(t);
  tabsRef.current = openedOrderIdList;
  historyRef.current = history;
  tRef.current = t;

  const dedupeRef = useRef(createDedupe());
  const idRef = useRef(0);

  const [permission, setPermission] = useState<PermissionState>(getPermissionState());

  const refreshPermission = () => {
    setPermission(getPermissionState());
  };

  const enableDesktopNotifications = async () => {
    setPermission(await requestNotificationPermission());
  };

  const [anchorNotifications, setAnchorNotifications] = useState<HTMLButtonElement | null>(null);

  const handleClickNotifications = (event: React.MouseEvent<HTMLButtonElement>) => {
    refreshPermission();
    setAnchorNotifications(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setAnchorNotifications(null);
    setNotifications((prev) => markAllViewed(prev));
  };

  const clearNotifications = () => {
    setNotifications(clearHistory());
  };

  const openOrderFromNotification = useCallback((orderId: string) => {
    const path = resolveOrderPath(orderId, tabsRef.current);

    if (path) {
      historyRef.current.push(path);
    }
  }, []);

  const openNotifications = Boolean(anchorNotifications);

  const [params, setParams] = useState<ListOrdersParams>({
    searchText: '',
    pagination: {
      items: 20,
      page: 1,
    },
  });

  const getOrders = useQuery([params, 'global-orders'], () => listOrders(params), {
    enabled: !!params.searchText && params.searchText.trim() !== '',
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const orders = useMemo(() => getOrders.data?.listOrders.orders || [], [getOrders.data]);
  const [searchResults, setSearchResults] = useState<OrderListingType[]>([]);

  const searchTextDebounce = useMemo(() => {
    return debounce((text: string) => {
      setParams((prevParams) => ({
        ...prevParams,
        searchText: text,
      }));
    }, 1000);
  }, []);

  const updateSearchText = useCallback(
    (text: string) => {
      setSearchResults([]);
      searchTextDebounce.clear();
      searchTextDebounce(text);
    },
    [searchTextDebounce]
  );

  useEffect(() => {
    setSearchResults(orders);
  }, [orders]);

  const handleSocketConnect = useCallback(() => {
    setIsConnectedSocket(true);
  }, []);

  const handleInfoUpdate = useCallback(
    (data: infoUpdateType) => {
      const now = Date.now();
      const result = evaluateEvent({ event: data, currentUser, openTabs: openedOrderIdList });

      if (!result.notify) {
        return;
      }

      if (!dedupeRef.current.shouldDeliver(`${result.tab.id}:${data.kind}`, now)) {
        return;
      }

      const orderId = result.tab.id;
      const orderCode = result.tab.code || result.tab.orderId;
      idRef.current += 1;
      const entry = {
        ...data,
        id: `${now}-${idRef.current}`,
        timestamp: now,
        update: { _id: orderId },
        orderCode,
      };

      setNotifications((prev) => appendHistory(prev, entry));

      const channel = decideChannel({
        supported: doesBrowserSupportsDesktopNotifications(),
        permission: getPermissionState(),
        visibilityState: document.visibilityState,
      });

      if (channel === 'desktop') {
        const wasShown = showDesktopNotification({
          content: buildDesktopContent({ t: tRef.current, event: entry, orderCode, icon: titleIcon }),
          onClick: (desktopNotification) => {
            window.focus();
            desktopNotification.close();
            openOrderFromNotification(orderId);
          },
        });

        if (!wasShown) {
          setNewNotification(entry);
        }

        return;
      }

      setNewNotification(entry);
    },
    [currentUser, openedOrderIdList, openOrderFromNotification]
  );

  const handleSocketDisconnect = useCallback(() => {
    setIsConnectedSocket(false);
  }, []);

  useInfoUpdateEvent(handleInfoUpdate);

  useEffect(() => {
    if (!socket || !socket.active) {
      return undefined;
    }

    socket.on('connect', handleSocketConnect);
    socket.on('disconnect', handleSocketDisconnect);

    return () => {
      socket.off('connect', handleSocketConnect);
      socket.off('disconnect', handleSocketDisconnect);
    };
  }, [socket, handleSocketConnect, handleSocketDisconnect]);

  return {
    isConnectedSocket,
    options: searchResults,
    loading: getOrders.isFetching,
    updateSearchText,
    notifications,
    newNotification,
    handleClickNotifications,
    anchorNotifications,
    openNotifications,
    handleCloseNotifications,
    currentUser,
    permission,
    enableDesktopNotifications,
    clearNotifications,
    openOrderFromNotification,
  };
};

export default useLayoutHeader;
